import React, { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { checkBotAccess } from "../../../shared/chatBot/chatBotSlice";

/**
 * Route guard for /midas -- redirects to home if bot is not enabled (SEC-REQ-2).
 * Must be wrapped inside AuthorizedRoute for auth check.
 *
 * On browser refresh Redux resets to initial state (botAccessChecked=false,
 * isBotEnabled=false). The guard dispatches checkBotAccess itself and shows
 * a loading spinner until the API responds, preventing a premature redirect.
 */
const MidasRouteGuard: React.FC = () => {
  const dispatch = useAppDispatch();
  const isBotEnabled = useAppSelector((state) => state.chatBot.isBotEnabled);
  const botAccessChecked = useAppSelector((state) => state.chatBot.botAccessChecked);

  // If bot access hasn't been checked yet (e.g. after browser refresh),
  // dispatch the check immediately rather than waiting for ChatWidget.
  useEffect(() => {
    if (!botAccessChecked) {
      dispatch(checkBotAccess());
    }
  }, [dispatch, botAccessChecked]);

  // Still waiting for the API response -- show a centered spinner
  if (!botAccessChecked) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Access checked and bot is disabled -- redirect to home
  if (!isBotEnabled) {
    return <Navigate to="/" replace />;
  }

  // Access checked and bot is enabled -- render the MIDAS page
  return <Outlet />;
};

export default MidasRouteGuard;
